<template>
  <div
    class="absolute z-10 flex w-[calc(100vw-104px)] flex-col bg-light-0 px-8 pb-6 dark:bg-dark-0"
  >
    <div class="flex justify-between">
      <div class="flex flex-row items-center pt-8 font-light leading-10">
        <a
          :class="[
            'flex cursor-pointer flex-row items-center not-prose',
            {
              'text-light-disabled-text dark:text-dark-disabled-text':
                isIncomplete,
            },
          ]"
          @click="goToBuildingOverview"
        >
          <em class="mdi mdi-chevron-left text-2xl leading-none" />
          {{ currentBuilding.shortAddress }}</a
        >
        <em class="mdi mdi-slash-forward" />
        <span>{{ firstRoom?.getFloor() }}</span>
        <em class="mdi mdi-slash-forward" />
        <span>{{ currentRoomgroup.ordinal }} </span>
      </div>
      <div
        v-if="lastSaved && savedToday"
        class="mr-[-28px] pt-1 text-sm dark:text-slate-400"
      >
        zuletzt gespeichert um {{ lastSavedTime }}
      </div>
    </div>
    <div class="ml-2 flex flex-row justify-between text-4xl font-bold">
      <div class="flex flex-row items-end">
        <div>
          <NoEntryIcon v-if="!currentRoomgroup?.isAccessible" />
          {{ currentRoomgroup.getLabel() }}
        </div>
        <div class="ml-3 pb-1 text-xs font-light text-gray-400">
          <div v-if="currentRoomgroup.number">
            Nr. {{ currentRoomgroup.number }}
          </div>
          <div v-if="currentRoomgroup.customerAssignmentReference">
            KDOB: {{ currentRoomgroup.customerAssignmentReference }}
          </div>
        </div>
      </div>
    </div>
    <div class="mt-2 flex flex-row gap-3">
      <TertiaryButton
        :icon="isRoomGroupComplete ? 'mdi mdi-check-bold' : ''"
        class="rounded-xl bg-primary-3 py-5 dark:bg-dark-1"
        data-testId="finishRoomGroupButton"
        :is-disabled="isBlocked"
        label="Fertig"
        @click="finishRoomgroup()"
      />
      <BaseInformation
        v-if="roomGroupFinishedClicked && unclearRooms.length > 0"
        :information-text="`In ${unclearRooms.length} Räumen sind keine Angaben zu den Geräten vorhanden`"
        status="error"
        data-testId="unclearRooms_info"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import NoEntryIcon from '@/components/UI/Badge/NoEntryIcon.vue';
import TertiaryButton from '@/components/UI/Button/TertiaryButton.vue';
import { useRouter } from 'vue-router';
import { routesNames } from '@/router';
import { computed } from 'vue';
import { useRoomGroupStore } from '@/store/entities/roomGroupStore';
import { useBuildingStore } from '@/store/entities/buildingStore';
import BaseInformation from '@/components/UI/Hint/BaseInformation.vue';
import useStores from '@/composables/useStores';
import { useRoomGroupProgress } from '@/composables/useRoomGroupProgress';
import { useLayoutStore } from '@/store/layout/layoutStore';
import { useDmgw6RoomGroupFunkcheck } from '@/composables/useDmgw6RoomGroupFunkcheck';

const { roomGroupStore, roomStore, orderStore } = useStores();

const currentRoomgroup = computed(() => {
  return useRoomGroupStore().getCurrentRoomGroup;
});
const currentBuilding = computed(() => {
  return useBuildingStore().getCurrentBuilding();
});
const roomsOfRoomgroup = computed(() => {
  if (!currentRoomgroup.value) return [];
  return roomStore.getRoomsByRoomGroupId(currentRoomgroup.value.id);
});

const { allDmgw6DevicesHaveStatus } =
  useDmgw6RoomGroupFunkcheck(roomsOfRoomgroup);

const firstRoom = computed(() => {
  if (!currentRoomgroup.value) return null;
  return roomStore.getFirstRoomOfRoomgroup(currentRoomgroup.value.id);
});

const roomGroupFinishedClicked = computed(() => {
  return roomGroupStore.isRoomGroupFinishedClicked(currentRoomgroup.value.id);
});

const unclearRooms = computed(() => {
  return roomsOfRoomgroup.value.filter((room) => {
    return !roomStore.isFinished(room.id);
  });
});

const isBlocked = computed(() => {
  if (!allDmgw6DevicesHaveStatus.value) return true;
  const incompleteRooms = roomsOfRoomgroup.value.filter((room) => {
    return !roomStore.isFinished(room.id);
  });
  return incompleteRooms.length > 0 && roomGroupFinishedClicked.value;
});

const isIncomplete = computed(() => {
  const incompleteRooms = roomsOfRoomgroup.value.filter((room) => {
    return roomStore.isIncomplete(room.id);
  });
  return incompleteRooms.length > 0 && roomGroupFinishedClicked.value;
});

const isInstallationOrderWithSmartDevices = computed(() => {
  return (
    orderStore.isInstallationOrder() &&
    orderStore.activeOrder?.hasSmartHeatingDevices()
  );
});

const requiresRoomValidation = computed(() => {
  if (isInstallationOrderWithSmartDevices.value) {
    return true;
  }
  return orderStore.isOnSiteInspection();
});

const isRoomGroupComplete = computed(() => {
  if (!currentRoomgroup.value) return false;
  return orderStore.isRoomGroupCompleted(currentRoomgroup.value);
});

const lastSaved = computed(() => {
  return orderStore?.activeOrder?.changes?.lastSaved;
});

const savedToday = computed(() => {
  return orderStore?.activeOrder?.changes?.savedToday;
});

const lastSavedTime = computed(() => {
  return orderStore?.activeOrder?.changes?.lastSavedTime;
});

const { completeRoomGroup } = useRoomGroupProgress(currentRoomgroup.value);

const finishRoomgroup = () => {
  if (
    unclearRooms.value.length === 0 ||
    currentRoomgroup.value?.usageType === 'GENERAL' ||
    !requiresRoomValidation.value
  ) {
    completeRoomGroup();
  } else {
    roomGroupStore.addToRoomGroupsFinishedClicked(currentRoomgroup.value?.id);
  }
};

const router = useRouter();
const goToBuildingOverview = () => {
  if (isIncomplete.value) return;
  if (useLayoutStore().isSandboxMode) {
    router.push({
      name: routesNames.SANDBOX_BUILDING_OVERVIEW,
    });
    return;
  }

  router.push({
    name: routesNames.BUILDING_OVERVIEW,
  });
};
</script>
